import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getGridNumericOperatorsWithNotLike } from '../../../Utils/DataGridFilterOperators';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import Constants from '../../../assests/Constants/constants';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { IconButton, Tooltip, Button, Chip } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
    flattenConversionRows,
    getHoistedReceiptRootIds,
    isConversionCancelBlocked,
    LIFECYCLE_STATUS,
    getProcessingStatus,
    getLifecycleAmounts,
    getStatusClassName,
    getStatusLabelKey,
} from '../documentLifecycle.js';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    width: '100%',
    '& .MuiAutocomplete-root': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: "center"
    },
    '& *': {
        boxSizing: 'border-box !important',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#E3E7FE',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#E3E7FE',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#1222b3',
        textAlign: 'center',
        fontFamily: 'Montserrat',
        fontSize: '14px',
        fontWeight: 600,
    },
    '& .MuiDataGrid-sortIcon': {
        opacity: '0.8 !important',
        color: '#1222b3',
    },
    '& .MuiDataGrid-menuIconButton': {
        color: '#1222b3',
    },
    '& .MuiDataGrid-cell': {
        borderTop: '1px solid rgba(191, 191, 193, 0.56)',
        backgroundColor: 'rgba(234, 237, 255, 0.32)',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        display: 'flex',
        justifyContent: 'center',
        alignItems: "center"
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(191, 200, 255, 0.3)',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .disableUser': {
        pointerEvents: 'none',
        opacity: 0.5,
    },
    '& .MuiDataGrid-cell--editing': {
        padding: 0,
        backgroundColor: 'inherit',
    },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
        borderRight: 'none',
    },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell:not(:last-child)': {
        borderRight: '8px solid transparent',
    },
    '& .canceled-row': {
        backgroundColor: 'rgba(255, 0, 0, 0.12)',
        color: '#a30000',
        fontWeight: 600,
    },
    '& .processed-row': {
        backgroundColor: 'rgba(0, 200, 83, 0.12)', // Light green
        color: '#1b5e20',
        fontWeight: 600,
    },
    '& .conversion-child-row.conversion-closed, & .conversion-grandchild-row.conversion-closed': {
        backgroundColor: 'rgba(42, 226, 119, 0.12)',
    },
    '& .conversion-child-row.conversion-closed .MuiDataGrid-cell, & .conversion-grandchild-row.conversion-closed .MuiDataGrid-cell': {
        backgroundColor: 'rgba(0, 200, 83, 0.12) !important',
    },
    '& .conversion-child-row.conversion-closed .MuiDataGrid-cell[data-field="conversionTree"], & .conversion-grandchild-row.conversion-closed .MuiDataGrid-cell[data-field="conversionTree"]': {
        borderInlineStart: '3px solid #43a047',
    },
    '& .conversion-child-row.conversion-open, & .conversion-grandchild-row.conversion-open': {
        backgroundColor: 'rgba(255, 152, 0, 0.12)',
    },
    '& .conversion-child-row.conversion-open .MuiDataGrid-cell, & .conversion-grandchild-row.conversion-open .MuiDataGrid-cell': {
        backgroundColor: 'rgba(241, 182, 93, 0.14) !important',
    },
    '& .conversion-child-row.conversion-open .MuiDataGrid-cell[data-field="conversionTree"], & .conversion-grandchild-row.conversion-open .MuiDataGrid-cell[data-field="conversionTree"]': {
        borderInlineStart: '3px solid rgb(247, 162, 59) !important',
    },
    '& .conversion-child-row.conversion-partial, & .conversion-grandchild-row.conversion-partial': {
        backgroundColor: 'rgba(33, 150, 243, 0.10)',
    },
    '& .conversion-child-row.conversion-partial .MuiDataGrid-cell, & .conversion-grandchild-row.conversion-partial .MuiDataGrid-cell': {
        backgroundColor: 'rgba(33, 150, 243, 0.10) !important',
    },
    '& .conversion-child-row.conversion-partial .MuiDataGrid-cell[data-field="conversionTree"], & .conversion-grandchild-row.conversion-partial .MuiDataGrid-cell[data-field="conversionTree"]': {
        borderInlineStart: '3px solid #1e88e5 !important',
    },
    '& .conversion-child-row.canceled-row .MuiDataGrid-cell, & .conversion-grandchild-row.canceled-row .MuiDataGrid-cell': {
        backgroundColor: 'rgba(255, 0, 0, 0.10) !important',
    },
}));

/**
 * Row `transactionDocumentType` that each pending-decision series can belong to. Reference
 * numbers are allocated per series, so a receipt can legitimately carry the same number as a
 * held tax invoice — matching on the number alone would flag it as needing a decision.
 */
const PENDING_DOC_TYPE_TO_ROW_TYPE = {
    TaxInvoice: 4,
    CreditNote: 5,
    ReceiptTax: 6,
    Transaction: 3,
};

/**
 * Document types that reach the Tax Authority at all. A plain receipt (7), quotation (1), order
 * (2) and the inventory documents never do, so they can never need a decision.
 */
const DECISION_CAPABLE_ROW_TYPES = [3, 4, 5, 6];

// Open/Closed does not apply to these types — hide the status chip and tooltip.
const STATUS_HIDDEN_ROW_TYPES = [5, 8, 9, 10, 12, 13, 14]; // CreditTaxInvoice, Deposit, DeferredReceipt, PrintedCheck, ReturnNote, StockRelease, StockIntake

const formatDate = (value) => {
    if (!value) return '';

    // If backend sends an ISO string with timezone (e.g. 2026-04-14T22:53:45+03:00),
    // formatting via Date() can shift the day depending on the viewer's timezone.
    // For "document date" we want the original calendar date, so we use the ISO date part.
    if (typeof value === 'string') {
        const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, year, month, day] = m;
            return `${day}/${month}/${year}`;
        }
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = (`0${date.getDate()}`).slice(-2);
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const TransactionDocumentDataGridComponent = (props) => {
    const { i18n, t } = useTranslation();
    const {
        data,
        openPdfInNewTab,
        direction,
        onActionTriggered,
        selectionModel,
        onSelectionChange,
        subAccountantOptions,
        onDownloadDocument,
        resolveContextMenuItems,
        pendingDecisionStatus,
        onOpenPendingDecision,
        allDocuments,
    } = props;

    const [rows, setRows] = useState([]);
    const [contextMenu, setContextMenu] = useState(null);
    const [contextMenuItems, setContextMenuItems] = useState([]);
    const [expandedMap, setExpandedMap] = useState({});
    const sourceRows = allDocuments || data || [];

    /* ------------------------------------------------------------------ */
    /* 1)  helper – add just **before** the `columns` array                */
    /* ------------------------------------------------------------------ */
    const getCancelActionForDocType = (docType) => Constants.CANCEL_ACTIONS1[docType];

    const handleDownloadDocument = async (row, downloadType = 'original') => {
        if (onDownloadDocument) {
            await onDownloadDocument(row, downloadType);
        }
    };
    useEffect(() => {
        const hoistedRootIds = getHoistedReceiptRootIds(data || [], sourceRows);
        if (!hoistedRootIds.length) return;
        setExpandedMap((prev) => {
            let changed = false;
            const next = { ...prev };
            hoistedRootIds.forEach((id) => {
                if (next[id] === undefined) {
                    next[id] = true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [data, sourceRows]);

    useEffect(() => {
        if (!data) {
            setRows([]);
            return;
        }
        setRows(flattenConversionRows(data, expandedMap, sourceRows));
    }, [data, expandedMap, i18n.language, sourceRows]);

    const toggleExpanded = (rowId) => {
        const id = String(rowId);
        setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleRowRightClick = (event) => {
        event.preventDefault();
        const rowId = event.currentTarget.getAttribute('data-id');
        const row = rows.find((r) => String(r.id) === rowId);
        if (row) {

            const desc = row.description || '';


            // const shouldBlockMenu =
            //     row.isCanceled ||
            //     (desc.includes("ביטול חשבונית מס קבלה מספר") || desc.includes("ביטול קבלה מספר"));

            const shouldBlockMenu = row.isCanceled;

            if (shouldBlockMenu) return; // Don't open the context menu

            setContextMenuItems(
                typeof resolveContextMenuItems === 'function'
                    ? resolveContextMenuItems(row)
                    : []
            );
            setContextMenu({
                mouseX: event.clientX - 2,
                mouseY: event.clientY - 4,
            });
        }
    };

    const handleMenuClose = () => {
        setContextMenu(null);
        setContextMenuItems([]);
    };

    /** Make sure we never forward IDs that are not in `rows`.
+  *  If the parent passes [], `sanitisedSelection` becomes [] ➜ grid shows nothing.
+  */
    const sanitisedSelection = useMemo(
        () => selectionModel.filter(id => rows.some(r => r.id === id)),
        [selectionModel, rows]
    );

    const numericFilterOperators = useMemo(() => getGridNumericOperatorsWithNotLike(t), [t]);
    const collapseIcon = direction === 'rtl'
        ? <KeyboardArrowLeftIcon fontSize="small" />
        : <KeyboardArrowRightIcon fontSize="small" />;

    const columns = [
        {
            field: 'conversionTree',
            headerName: '',
            headerAlign: 'center',
            align: 'center',
            width: 34,
            minWidth: 34,
            maxWidth: 34,
            sortable: false,
            filterable: false,
            resizable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const depth = params.row.conversionDepth || 0;
                const hasChildren = params.row.conversionHasChildren;
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            paddingInlineStart: depth * 6,
                        }}
                    >
                        {hasChildren ? (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpanded(params.row.id);
                                }}
                                title={params.row.conversionExpanded ? t('Collapse') : t('Expand')}
                                sx={{
                                    p: 0.25,
                                    color: '#1222b3',
                                    backgroundColor: params.row.conversionExpanded ? 'rgba(48, 79, 255, 0.12)' : 'transparent',
                                }}
                            >
                                {params.row.conversionExpanded
                                    ? <KeyboardArrowDownIcon fontSize="small" />
                                    : collapseIcon}
                            </IconButton>
                        ) : depth > 0 ? (
                            <span style={{ width: 24, display: 'inline-block' }} />
                        ) : null}
                    </div>
                );
            },
        },
        {
            field: 'document',
            headerName: t('Document'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <div
                    className="iframe-container"
                    onClick={() => openPdfInNewTab(params.row.documentUrl)}
                    style={{ cursor: 'pointer' }}
                    title={t('RightClickForActions')}
                >
                    <iframe
                        className="zoom-iframe"
                        src={
                            params.row.documentUrl
                                ? `${params.row.documentUrl}#page=1`
                                : `${NoDocumentsPdf}#page=1`
                        }
                        width="100px"
                        height="50px"
                        title={params.row.title}
                        style={{ pointerEvents: 'none' }}
                    />
                </div>
            ),
        },
        {
            field: 'transactionDocumentType',
            headerName: t('DocumentType'),
            headerAlign: 'center',
            align: 'center',
            flex: 1.2,
            type: 'string',
            renderCell: (params) => (
                <Tooltip title={t('RightClickForActions')} arrow>
                    <span>{Constants.TransactionDocuments[params.value]}</span>
                </Tooltip>
            ),
        },
        {
            field: 'isPrintedCheck',
            headerName: t('PrintedCheck'),
            headerAlign: 'center',
            align: 'center',
            width: 130,
            type: 'string',
            renderCell: (params) => (
                <Tooltip title={t('RightClickForActions')} arrow>
                    <span>{params.value ? t('Yes') : ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'accountCodeName',
            headerName: t('Client Name'),
            headerAlign: 'center',
            align: 'center',
            flex: 1.2,
            type: 'string',
            renderCell: (params) => (
                <Tooltip title={`${params.value?.trim() || ''} - ${t('RightClickForActions')}`} arrow>
                    <span
                        style={{
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                            maxHeight: '42px',
                            lineHeight: '21px',
                        }}
                    >
                        {params.value?.trim() || ''}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'documentDate',
            headerName: t('Document Date'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            type: 'string',
            renderCell: (params) => (
                <Tooltip title={t('RightClickForActions')} arrow>
                    <span>{formatDate(params.value)}</span>
                </Tooltip>
            ),
        },
        {
            field: 'accountCaseId',
            headerName: t('CompanyId'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            type: 'string',
            renderCell: (params) => (
                <Tooltip title={t('RightClickForActions')} arrow>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'reference',
            headerName: t('Reference'),
            headerAlign: 'center',
            align: 'center',
            width: 100,
            type: 'number',
            renderCell: (params) => (
                <Tooltip title={t('RightClickForActions')} arrow>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'description',
            headerName: t('Description'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            type: 'string',
            renderCell: (params) => (
                <Tooltip title={params.value?.trim() || ''} arrow>
                    <span
                        style={{
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                            maxHeight: '42px',
                            lineHeight: '21px',
                        }}
                    >
                        {params.value?.trim() || ''}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'amount',
            headerName: t('Amount'),
            headerAlign: 'center',
            align: 'center',
            width: 100,
            type: 'number',
            filterOperators: numericFilterOperators,
            renderCell: (params) => {
                const baseAmount = params.value ?? 0;
                const withholdingTax = params.row.withholdingTax ?? 0;
                const totalAmount = baseAmount ;

                return (
                    <Tooltip title={t('RightClickForActions')} arrow>
                        <span>{beautifyNumber(totalAmount)}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'generatedBy',
            headerName: t('GeneratedBy'),
            headerAlign: 'center',
            align: 'center',
            width: 100,
            type: 'string',
            renderCell: (params) => {
                const accountant = subAccountantOptions.find(option => option.value === params.value);
                return (


                    <Tooltip title={t('RightClickForActions')} arrow>
                        <span
                            style={{
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                wordBreak: 'break-word',
                                maxWidth: '100%',
                                maxHeight: '42px',
                                lineHeight: '21px',
                            }}
                        >
                            <span>{accountant ? accountant.label : '-'}</span>
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'status',
            headerName: t('Status'),
            headerAlign: 'center',
            align: 'center',
            width: 120,
            type: 'singleSelect',
            valueGetter: (_value, row) => getProcessingStatus(row),
            valueOptions: [
                { value: LIFECYCLE_STATUS.OPEN, label: t('DocumentStatusOpen') },
                { value: LIFECYCLE_STATUS.PARTIAL, label: t('DocumentStatusPartial') },
                { value: LIFECYCLE_STATUS.CLOSED, label: t('DocumentStatusClosed') },
            ],
            renderCell: (params) => {
                const rowType = Number(params.row?.transactionDocumentType);
                if (params.row?.isCanceled || STATUS_HIDDEN_ROW_TYPES.includes(rowType)) {
                    return null;
                }
                const amounts = getLifecycleAmounts(params.row);
                const tooltip = (
                    <span style={{ whiteSpace: 'pre-line' }}>
                        {amounts && getProcessingStatus(params.row) !== LIFECYCLE_STATUS.OPEN
                            ? [
                                `${t('DocumentLifecycleOriginalAmount')}: ${amounts.original}`,
                                `${t('DocumentLifecycleAppliedAmount')}: ${amounts.applied}`,
                                `${t('DocumentLifecycleRemainingAmount')}: ${amounts.remaining ?? '-'}`,
                                ...(amounts.hasUnresolvedAllocation ? [t('DocumentLifecycleUnresolvedAllocation')] : []),
                                t('RightClickForActions'),
                            ].join('\n')
                            : t('RightClickForActions')}
                    </span>
                );
                return (
                    <Tooltip title={tooltip} arrow>
                        <span className={getStatusClassName(params.row)}>
                            {t(getStatusLabelKey(params.row))}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'isCanceled',
            headerName: t('DocumentStatusExistingCanceled'),
            headerAlign: 'center',
            align: 'center',
            width: 130,
            type: 'string',
            renderCell: (params) => {
                const pendingNeeds =
                    pendingDecisionStatus?.requiresDecision || pendingDecisionStatus?.RequiresDecision;
                const pendingInvoiceId = String(
                    pendingDecisionStatus?.invoiceId || pendingDecisionStatus?.InvoiceId || ''
                );
                const rowRef = String(params.row?.reference || params.row?.Reference || '');
                const pendingDocType =
                    pendingDecisionStatus?.documentType || pendingDecisionStatus?.DocumentType || '';
                const expectedRowType = PENDING_DOC_TYPE_TO_ROW_TYPE[pendingDocType];
                const rowType = Number(params.row?.transactionDocumentType);
                // Restrict to the pending series; without a known series fall back to the types
                // that can reach the Tax Authority, so a receipt is excluded either way.
                const rowTypeMatches =
                    expectedRowType != null
                        ? rowType === expectedRowType
                        : DECISION_CAPABLE_ROW_TYPES.includes(rowType);
                const isPendingRef =
                    pendingNeeds &&
                    pendingInvoiceId &&
                    rowRef &&
                    rowTypeMatches &&
                    (rowRef === pendingInvoiceId || params.row?.id === pendingInvoiceId);

                if (params.value) {
                    return (
                        <Tooltip title={t('RightClickForActions')} arrow>
                            <span style={{ color: 'red', fontWeight: 'bold' }}>{t('Canceled')}</span>
                        </Tooltip>
                    );
                }
                if (isPendingRef) {
                    return (
                        <Tooltip
                            title={
                                pendingDecisionStatus?.firstAttempt?.errorMessage ||
                                pendingDecisionStatus?.FirstAttempt?.ErrorMessage ||
                                t('PendingTaxAuthorityDecision') ||
                                'Pending decision'
                            }
                            arrow
                        >
                            <Chip
                                size="small"
                                color="warning"
                                label={t('NeedsDecision') || 'Needs decision'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenPendingDecision) onOpenPendingDecision();
                                }}
                                sx={{ cursor: onOpenPendingDecision ? 'pointer' : 'default' }}
                            />
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={t('RightClickForActions')} arrow>
                        <span />
                    </Tooltip>
                );
            },
        },
        {
            field: 'actions',
            headerName: '',                // no header text
            headerAlign: 'center',
            align: 'center',
            width: 90,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const row = params.row;
                const cancelAction = getCancelActionForDocType(row.transactionDocumentType);

                // hide button if the row is already canceled, no cancel action exists,
                // or a live Receipt / InvoiceReceipt / TaxInvoice still sits above it
                if (row.isCanceled || !cancelAction) return null;
                if (isConversionCancelBlocked(row, sourceRows)) return null;

                return (
                    <Tooltip title={t('Cancel')} arrow>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                onActionTriggered(cancelAction, row);
                            }}
                            size="small"
                            variant="text"
                            endIcon={<HighlightOffIcon fontSize="small" />}
                            sx={{
                                color: '#ff1744', // MUI error color
                                fontWeight: 600,
                                textTransform: 'none',
                                minWidth: 'unset',
                                padding: '2px 6px',
                                '&:hover': {
                                    color: '#d50000',
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            {t('Cancel')}
                        </Button>
                    </Tooltip>
                );
            },
        },
    ];


    return (
        <div className={`${direction}`} style={{ width: '100%', height: '60vh' }}>
            <StyledDataGrid
                rows={rows}
                columns={columns}
                disableSelectionOnClick
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                getRowClassName={(params) => {
                    const classes = [];
                    if (params.row.isCanceled) classes.push('canceled-row');
                    else if (params.row.transactionDocumentType >= 11 && params.row.transactionDocumentType <= 14 && params.row.isProcessed) {
                        classes.push('processed-row');
                    }
                    if (params.row.conversionDepth === 1) classes.push('conversion-child-row');
                    if (params.row.conversionDepth >= 2) classes.push('conversion-grandchild-row');
                    if (params.row.conversionDepth >= 1) {
                        const lifecycleStatus = getProcessingStatus(params.row);
                        classes.push(
                            lifecycleStatus === LIFECYCLE_STATUS.CLOSED
                                ? 'conversion-closed'
                                : lifecycleStatus === LIFECYCLE_STATUS.PARTIAL
                                    ? 'conversion-partial'
                                    : 'conversion-open'
                        );
                    }
                    return classes.join(' ');
                }}
                components={{ Toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                    row: {
                        onContextMenu: handleRowRightClick,
                        style: {
                            cursor: 'context-menu'
                        },

                    }
                }}
                pagination
                disableColumnSorting
                sortingOrder={[]}
                checkboxSelection
                selectionModel={selectionModel}
                onRowSelectionModelChange={onSelectionChange}
                rowSelectionModel={sanitisedSelection}

            />

            <Menu
                open={contextMenu !== null}
                onClose={handleMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                {contextMenuItems.map((item, index) => (
                    item.type === 'separator' ? (
                        <div key={`sep-${index}`} style={{ borderTop: '1px solid #e0e0e0', margin: '4px 0' }} />
                    ) : (
                        <MenuItem
                            key={`${item.label}-${index}`}
                            onClick={() => {
                                item.onClick?.();
                                handleMenuClose();
                            }}
                            className={`${direction}`}
                        >
                            {item.label}
                        </MenuItem>
                    )
                ))}
            </Menu>
        </div>
    );
};

export default TransactionDocumentDataGridComponent;

